import {useState} from 'react'
import {useParams} from 'react-router-dom'
import {useDialog} from '@/hooks/useDialog'
import {DndContext, PointerSensor, useSensor, useSensors, closestCenter} from '@dnd-kit/core'
import {SortableContext, verticalListSortingStrategy, useSortable} from '@dnd-kit/sortable'
import {CSS} from '@dnd-kit/utilities'
import {Pencil, Plus, Trash2} from 'lucide-react'
import {routes} from '@/lib/routes'
import {notFoundIfMissing} from '@/lib/not-found-error'
import {useI18n} from '@/i18n'
import {MutedText} from '@/components/shared/MutedText'
import {Stack} from '@/components/shared/Stack'
import {InlineRow} from '@/components/shared/InlineRow'
import {CodeText} from '@/components/shared/CodeText'
import {useAuth} from '@/contexts/auth-context'
import {permissions} from '@/lib/permissions'
import {Button} from '@/components/ui/button'
import {Badge, BadgeVariant} from '@/components/ui/badge'
import {Card, CardContent} from '@/components/ui/card'
import {DetailsCard} from '@/components/shared/DetailsCard'
import {PageLayout} from '@/components/shared/PageLayout'
import {RowCardHeader} from '@/components/shared/RowCardHeader'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {CONCEPT_ICON} from '@/lib/concepts'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {Switch} from '@/components/ui/switch'
import {Textarea} from '@/components/ui/textarea'
import type {ValueListEntry} from '@/types/value-list'
import {cn} from '@/lib/utils'
import {FormGrid} from '@/components/shared/FormGrid'
import {FormField} from '@/components/shared/FormField'
import {SaveCancelFooter} from '@/components/shared/SaveCancelFooter'
import {DragHandle} from '@/components/shared/DragHandle'
import {Icon} from '@/components/shared/Icon'
import {Strong} from '@/components/shared/Strong'
import {InlineEmptyText} from '@/components/shared/InlineEmptyText'
import {useValueList} from '@/features/value-lists/hooks/useValueList'
import {useValueListMutations} from '@/features/value-lists/hooks/useValueListMutations'
import {useEntryReorder} from '@/features/value-lists/hooks/useEntryReorder'

function SortableEntryRow({
    entry,
    canUpdate,
    onEdit,
    onDelete,
}: {
    entry: ValueListEntry
    canUpdate: boolean
    onEdit: (entry: ValueListEntry) => void
    onDelete: (entry: ValueListEntry) => void
}) {
    const i18n = useI18n()
    const {attributes, listeners, setNodeRef, transform, transition, isDragging} = useSortable({
        id: entry.id,
        disabled: !canUpdate,
    })

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    }

    return (
        <TableRow
            ref={setNodeRef}
            style={style}
            data-testid={`entry-row-${entry.id}`}
            className={cn(isDragging && 'bg-muted')}
        >
            <TableCell className="w-8">
                {canUpdate && (
                    <DragHandle
                        {...attributes}
                        {...listeners}
                        title={i18n.valueLists.entriesTable.dragToReorder}
                    />
                )}
            </TableCell>
            <TableCell className="font-mono text-sm">{entry.code}</TableCell>
            <TableCell>{entry.label}</TableCell>
            <TableCell className="text-muted-foreground">{entry.description ?? '—'}</TableCell>
            <TableCell>
                <Badge variant={entry.active ? BadgeVariant.SUCCESS : BadgeVariant.WARNING}>
                    {entry.active ? i18n.common.active : i18n.common.inactive}
                </Badge>
            </TableCell>
            <TableCell className="text-right">
                {canUpdate && (
                    <InlineRow justify="end" gap="xs">
                        <Button
                            data-testid={`btn-edit-entry-${entry.id}`}
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => onEdit(entry)}
                        >
                            <Pencil className="h-3.5 w-3.5"/>
                        </Button>
                        <Button
                            data-testid={`btn-delete-entry-${entry.id}`}
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => onDelete(entry)}
                        >
                            <Icon icon={Trash2} className="text-destructive"/>
                        </Button>
                    </InlineRow>
                )}
            </TableCell>
        </TableRow>
    )
}

interface EntryFormState {
    code: string
    label: string
    description: string
    active: boolean
    version?: number
}

const EMPTY_ENTRY_FORM: EntryFormState = {code: '', label: '', description: '', active: true}

export function ValueListDetail() {
    const i18n = useI18n()
    const {id} = useParams<{id: string}>()
    const {hasPermission} = useAuth()

    const canUpdate = hasPermission(permissions.valueLists.update)
    const canDelete = hasPermission(permissions.valueLists.delete)

    const addEntryDialog = useDialog()
    const [editEntry, setEditEntry] = useState<ValueListEntry | null>(null)
    const [deleteEntry, setDeleteEntry] = useState<ValueListEntry | null>(null)
    const editListDialog = useDialog()
    const deleteListDialog = useDialog()

    const [entryForm, setEntryForm] = useState<EntryFormState>(EMPTY_ENTRY_FORM)
    const [editListForm, setEditListForm] = useState({
        displayName: '', description: '', active: true, version: 0,
    })

    const {list, isLoading, error} = useValueList(id)

    const reorder = useEntryReorder(id, list?.entries ?? [])
    const entries = reorder.entries

    const dndSensors = useSensors(useSensor(PointerSensor, {activationConstraint: {distance: 5}}))

    const mutations = useValueListMutations(id, {
        onAddEntrySuccess: () => {
            reorder.resetLocalOrder()
            addEntryDialog.close()
            setEntryForm(EMPTY_ENTRY_FORM)
        },
        onUpdateEntrySuccess: () => {
            reorder.resetLocalOrder()
            setEditEntry(null)
        },
        onDeleteEntrySuccess: () => {
            reorder.resetLocalOrder()
            setDeleteEntry(null)
        },
        onUpdateListSuccess: () => {
            editListDialog.close()
        },
    })

    function openEditEntry(entry: ValueListEntry) {
        setEntryForm({
            code: entry.code,
            label: entry.label,
            description: entry.description ?? '',
            active: entry.active,
            version: entry.version,
        })
        setEditEntry(entry)
    }

    function openEditList() {
        if (!list) return
        setEditListForm({
            displayName: list.displayName,
            description: list.description ?? '',
            active: list.active,
            version: list.version,
        })
        editListDialog.open()
    }

    return (
        <PageLayout
            data-testid="value-list-detail-page"
            variant="detail"
            title={list?.displayName ?? i18n.valueLists.detailPageTitle}
            description={list?.name}
            icon={CONCEPT_ICON.valueList}
            loading={isLoading}
            error={error ?? (!isLoading ? notFoundIfMissing(list, 'VALUE_LIST') : undefined)}
            backTo={routes.schema.valueLists.list()}
            actions={list && (canUpdate || canDelete) ? (
                <InlineRow>
                    {canUpdate && (
                        <Button
                            data-testid="btn-edit-list"
                            variant="outline"
                            onClick={openEditList}
                        >
                            <Icon icon={Pencil} className="mr-2"/>
                            {i18n.valueLists.actions.editList}
                        </Button>
                    )}
                    {canDelete && (
                        <Button
                            data-testid="btn-delete-list"
                            variant="destructive"
                            onClick={deleteListDialog.open}
                        >
                            <Icon icon={Trash2} className="mr-2"/>
                            {i18n.valueLists.actions.deleteList}
                        </Button>
                    )}
                </InlineRow>
            ) : undefined}
        >
            {list && <>
            <Stack gap="lg">
                <DetailsCard
                    data-testid="card-value-list-details"
                    title={i18n.valueLists.columns.description}
                    fields={[
                        {label: i18n.valueLists.columns.name, value: <CodeText>{list.name}</CodeText>},
                        {label: i18n.valueLists.columns.displayName, value: list.displayName},
                        ...(list.description ? [{label: i18n.valueLists.columns.description, value: list.description, fullWidth: true as const}] : []),
                    ]}
                />

                <Card>
                    <RowCardHeader>
                        <Strong weight="semibold">{i18n.valueLists.entriesTable.title}</Strong>
                        {canUpdate && (
                            <Button
                                data-testid="btn-add-entry"
                                size="sm"
                                onClick={() => {
                                    setEntryForm(EMPTY_ENTRY_FORM)
                                    addEntryDialog.open()
                                }}
                            >
                                <Icon icon={Plus} className="mr-2"/>
                                {i18n.valueLists.actions.addEntry}
                            </Button>
                        )}
                    </RowCardHeader>
                    <CardContent>
                        {entries.length === 0 ? (
                            <InlineEmptyText data-testid="entries-empty">
                                {i18n.valueLists.entriesTable.empty}
                            </InlineEmptyText>
                        ) : (
                            <DndContext
                                sensors={dndSensors}
                                collisionDetection={closestCenter}
                                onDragEnd={reorder.handleDragEnd}
                            >
                                <SortableContext
                                    items={entries.map(e => e.id)}
                                    strategy={verticalListSortingStrategy}
                                >
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead data-testid="col-drag" className="w-8"/>
                                                <TableHead data-testid="col-code">{i18n.valueLists.entriesTable.code}</TableHead>
                                                <TableHead data-testid="col-label">{i18n.valueLists.entriesTable.label}</TableHead>
                                                <TableHead data-testid="col-description">{i18n.valueLists.entriesTable.description}</TableHead>
                                                <TableHead data-testid="col-active">{i18n.valueLists.entriesTable.active}</TableHead>
                                                <TableHead data-testid="col-actions"/>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {entries.map(entry => (
                                                <SortableEntryRow
                                                    key={entry.id}
                                                    entry={entry}
                                                    canUpdate={canUpdate}
                                                    onEdit={openEditEntry}
                                                    onDelete={e => setDeleteEntry(e)}
                                                />
                                            ))}
                                        </TableBody>
                                    </Table>
                                </SortableContext>
                            </DndContext>
                        )}
                    </CardContent>
                </Card>
            </Stack>

            <Dialog open={addEntryDialog.isOpen} onOpenChange={addEntryDialog.bind.onOpenChange}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{i18n.valueLists.form.addEntryTitle}</DialogTitle>
                        <DialogDescription>{i18n.valueLists.form.addEntryDescription}</DialogDescription>
                    </DialogHeader>
                    <FormGrid>
                        <FormField htmlFor="entry-code" label={i18n.valueLists.entriesTable.code}>
                            <Input
                                id="entry-code"
                                data-testid="input-entry-code"
                                value={entryForm.code}
                                onChange={e => setEntryForm(f => ({...f, code: e.target.value}))}
                                placeholder={i18n.valueLists.form.entryCodePlaceholder}
                            />
                            <MutedText size="xs">{i18n.valueLists.form.entryCodeHelp}</MutedText>
                        </FormField>
                        <FormField htmlFor="entry-label" label={i18n.valueLists.entriesTable.label}>
                            <Input
                                id="entry-label"
                                data-testid="input-entry-label"
                                value={entryForm.label}
                                onChange={e => setEntryForm(f => ({...f, label: e.target.value}))}
                                placeholder={i18n.valueLists.form.entryLabelPlaceholder}
                            />
                        </FormField>
                        <FormField htmlFor="entry-description" label={i18n.valueLists.entriesTable.description}>
                            <Textarea
                                id="entry-description"
                                data-testid="input-entry-description"
                                value={entryForm.description}
                                onChange={e => setEntryForm(f => ({...f, description: e.target.value}))}
                                placeholder={i18n.valueLists.form.entryDescriptionPlaceholder}
                                rows={2}
                            />
                        </FormField>
                    </FormGrid>
                    <SaveCancelFooter
                        testIdPrefix="add-entry"
                        onCancel={addEntryDialog.close}
                        saving={mutations.addEntry.isPending}
                        saveLabel={i18n.common.add}
                        disabled={!entryForm.code || !entryForm.label}
                        saveType="button"
                        onSave={() => mutations.addEntry.mutate({
                            code: entryForm.code,
                            label: entryForm.label,
                            description: entryForm.description || null,
                        })}
                    />
                </DialogContent>
            </Dialog>

            <Dialog open={!!editEntry} onOpenChange={open => { if (!open) setEditEntry(null) }}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{i18n.valueLists.form.editEntryTitle}</DialogTitle>
                        <DialogDescription>{i18n.valueLists.form.editEntryDescription}</DialogDescription>
                    </DialogHeader>
                    <FormGrid>
                        <FormField htmlFor="edit-entry-code" label={i18n.valueLists.entriesTable.code}>
                            <Input
                                id="edit-entry-code"
                                value={entryForm.code}
                                disabled
                                className="font-mono"
                            />
                        </FormField>
                        <FormField htmlFor="edit-entry-label" label={i18n.valueLists.entriesTable.label}>
                            <Input
                                id="edit-entry-label"
                                data-testid="input-edit-entry-label"
                                value={entryForm.label}
                                onChange={e => setEntryForm(f => ({...f, label: e.target.value}))}
                                placeholder={i18n.valueLists.form.entryLabelPlaceholder}
                            />
                        </FormField>
                        <FormField htmlFor="edit-entry-description" label={i18n.valueLists.entriesTable.description}>
                            <Textarea
                                id="edit-entry-description"
                                data-testid="input-edit-entry-description"
                                value={entryForm.description}
                                onChange={e => setEntryForm(f => ({...f, description: e.target.value}))}
                                placeholder={i18n.valueLists.form.entryDescriptionPlaceholder}
                                rows={2}
                            />
                        </FormField>
                        <InlineRow gap="md">
                            <Switch
                                id="edit-entry-active"
                                data-testid="switch-edit-entry-active"
                                checked={entryForm.active}
                                onCheckedChange={active => setEntryForm(f => ({...f, active}))}
                            />
                            <Label htmlFor="edit-entry-active">{i18n.valueLists.entriesTable.active}</Label>
                        </InlineRow>
                    </FormGrid>
                    <SaveCancelFooter
                        testIdPrefix="edit-entry"
                        onCancel={() => setEditEntry(null)}
                        saving={mutations.updateEntry.isPending}
                        disabled={!entryForm.label}
                        saveType="button"
                        onSave={() => editEntry && mutations.updateEntry.mutate({
                            entryId: editEntry.id,
                            label: entryForm.label,
                            description: entryForm.description || null,
                            active: entryForm.active,
                            version: entryForm.version ?? editEntry.version,
                        })}
                    />
                </DialogContent>
            </Dialog>

            <Dialog open={!!deleteEntry} onOpenChange={open => { if (!open) setDeleteEntry(null) }}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{i18n.valueLists.actions.deleteEntry}</DialogTitle>
                        <DialogDescription>{i18n.valueLists.messages.confirmDeleteEntry}</DialogDescription>
                    </DialogHeader>
                    <SaveCancelFooter
                        testIdPrefix="delete-entry"
                        onCancel={() => setDeleteEntry(null)}
                        saving={mutations.deleteEntry.isPending}
                        saveLabel={i18n.common.delete}
                        savingLabel={i18n.common.deleting}
                        destructive
                        saveType="button"
                        onSave={() => deleteEntry && mutations.deleteEntry.mutate(deleteEntry.id)}
                    />
                </DialogContent>
            </Dialog>

            <Dialog open={editListDialog.isOpen} onOpenChange={editListDialog.bind.onOpenChange}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{i18n.valueLists.form.editTitle}</DialogTitle>
                        <DialogDescription>{i18n.valueLists.form.editDescription}</DialogDescription>
                    </DialogHeader>
                    <FormGrid>
                        <FormField htmlFor="edit-vl-display-name" label={i18n.valueLists.columns.displayName}>
                            <Input
                                id="edit-vl-display-name"
                                data-testid="input-edit-vl-display-name"
                                value={editListForm.displayName}
                                onChange={e => setEditListForm(f => ({...f, displayName: e.target.value}))}
                                placeholder={i18n.valueLists.form.displayNamePlaceholder}
                            />
                        </FormField>
                        <FormField htmlFor="edit-vl-description" label={i18n.valueLists.columns.description}>
                            <Textarea
                                id="edit-vl-description"
                                data-testid="input-edit-vl-description"
                                value={editListForm.description}
                                onChange={e => setEditListForm(f => ({...f, description: e.target.value}))}
                                placeholder={i18n.valueLists.form.descriptionPlaceholder}
                                rows={3}
                            />
                        </FormField>
                        <InlineRow gap="md">
                            <Switch
                                id="edit-vl-active"
                                data-testid="switch-edit-vl-active"
                                checked={editListForm.active}
                                onCheckedChange={active => setEditListForm(f => ({...f, active}))}
                            />
                            <Label htmlFor="edit-vl-active">{i18n.valueLists.columns.active}</Label>
                        </InlineRow>
                    </FormGrid>
                    <SaveCancelFooter
                        testIdPrefix="edit-vl"
                        onCancel={editListDialog.close}
                        saving={mutations.updateList.isPending}
                        disabled={!editListForm.displayName}
                        saveType="button"
                        onSave={() => mutations.updateList.mutate({
                            displayName: editListForm.displayName,
                            description: editListForm.description || null,
                            active: editListForm.active,
                            version: editListForm.version,
                        })}
                    />
                </DialogContent>
            </Dialog>

            <Dialog open={deleteListDialog.isOpen} onOpenChange={deleteListDialog.bind.onOpenChange}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{i18n.valueLists.actions.deleteList}</DialogTitle>
                        <DialogDescription>{i18n.valueLists.messages.confirmDeleteList}</DialogDescription>
                    </DialogHeader>
                    <SaveCancelFooter
                        testIdPrefix="delete-list"
                        onCancel={deleteListDialog.close}
                        saving={mutations.deleteList.isPending}
                        saveLabel={i18n.common.delete}
                        savingLabel={i18n.common.deleting}
                        destructive
                        saveType="button"
                        onSave={() => mutations.deleteList.mutate()}
                    />
                </DialogContent>
            </Dialog>
        </>}
        </PageLayout>
    )
}
